import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  User,  LogOut, Bell,
  Shield, HelpCircle,  Crown, ChevronRight, Phone, Mail
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (!error) setProfile(data);
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50 text-emerald-600 font-bold uppercase tracking-widest">
      <motion.div transition={{ repeat: Infinity, ease: "linear" }} className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-500 rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-6xl mx-auto p-4 md:p-8 lg:p-12 pb-32"
      >
        {/* TOP BAR */}
        <div className="flex justify-between items-center mb-10">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">Account</h2>
          <div className="flex items-center gap-3">
             <div className={`hidden md:flex px-4 py-2 rounded-2xl font-black text-[10px] uppercase tracking-widest border ${
               profile?.premium 
               ? "bg-amber-50 text-amber-600 border-amber-200" 
               : "bg-gray-100 text-gray-400 border-gray-200"
             }`}>
               {profile?.premium ? "Pro Member" : "Free Plan"}
             </div>
             <motion.button whileTap={{ scale: 0.9 }} className="p-3 bg-white shadow-sm border border-gray-100 rounded-2xl text-gray-400">
              <Bell size={22} />
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT COLUMN */}
          <div className="lg:col-span-1 space-y-6">
            <div className={`relative overflow-hidden rounded-[32px] p-8 shadow-2xl text-white transition-all duration-500 ${
              profile?.premium 
              ? "bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900 shadow-emerald-100" 
              : "bg-gradient-to-br from-emerald-600 to-teal-700 shadow-emerald-50"
            }`}>
              {profile?.premium && (
                <div className="absolute top-0 right-0 p-6 opacity-20">
                
                </div>
              )}
              {/*  <Crown size={90} /> */}
              <div className="flex flex-col items-center text-center relative z-10">
                <div className="w-24 h-24 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center border border-white/30 shadow-inner mb-4">
                  <User className="w-12 h-12 text-white" />
                </div>
                
                <div className="flex flex-col items-center gap-1">
                  <h3 className="text-2xl font-black capitalize flex items-center gap-2">
                    {profile?.name || "Fitness Pro"}
                    {profile?.premium && <Crown size={20} className="text-amber-400 fill-amber-400" />}
                  </h3>
                  
                  <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full backdrop-blur-md border ${
                    profile?.premium 
                    ? "bg-amber-400/20 text-amber-300 border-amber-400/30" 
                    : "bg-white/10 text-emerald-100 border-white/20"
                  }`}>
                    {profile?.premium ? "Premium Active" : "Standard Account"}
                  </span>
                </div>

                <div className="mt-4 space-y-1 opacity-80">
                  <p className="text-sm font-medium flex items-center justify-center gap-2"><Mail size={14}/> {user?.email}</p>
                  {profile?.phone && <p className="text-xs font-bold flex items-center justify-center gap-2"><Phone size={12}/> {profile.phone}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-8 relative z-10">
                <StatCard label="Current" value={profile?.weight} unit="kg" />
                <StatCard label="Goal" value={profile?.target_weight} unit="kg" />
                <StatCard label="Height" value={profile?.height} unit="cm" />
                <StatCard label="Age" value={profile?.age} unit="yrs" />
              </div>

              {!profile?.premium && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/premium")}
                  className="w-full mt-6 py-4 bg-amber-400 text-amber-950 font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-amber-900/20 flex items-center justify-center gap-2"
                >
                  <Crown size={16} /> Upgrade to Pro
                </motion.button>
              )}
            </div>

            <motion.button
              onClick={handleSignOut}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center space-x-3 p-5 bg-white border border-red-100 text-red-500 rounded-[24px] font-black text-sm uppercase tracking-widest transition-all hover:bg-red-50"
            >
              <LogOut size={20} />
              <span>Sign Out</span>
            </motion.button>
          </div>

          {/* RIGHT COLUMN     <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ActionButton icon={Target} label="Goals" color="bg-orange-50 text-orange-600" />
              <ActionButton icon={Activity} label="Activity" color="bg-blue-50 text-blue-600" />
              <ActionButton icon={Smartphone} label="Devices" color="bg-purple-50 text-purple-600" />
              <ActionButton 
                icon={Shield} 
                label="Privacy" 
                color="bg-emerald-50 text-emerald-600" 
                onClick={() => navigate("/privacy-policy")} 
              />
            </div>*/}
          <div className="lg:col-span-2 space-y-8">
          

            <div className="bg-white rounded-[40px] p-6 md:p-10 shadow-sm border border-gray-50">
              <h4 className="px-4 pb-6 text-xs font-black text-gray-300 uppercase tracking-[0.3em]">System Preferences</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <SettingItem icon={User} label="Personal Information" onClick={() => navigate("/personal-info")} />
                <SettingItem icon={Bell} label="FeedBack" onClick={() => navigate("/feedback")}/>
                <SettingItem icon={Shield} label="Privacy Policy" onClick={() => navigate("/privacy-policy")} />
                <SettingItem icon={HelpCircle} label="Help & FAQ" onClick={() => navigate("/help-faq")}/>
             
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/* --- HELPER COMPONENTS --- */

const StatCard = ({ label, value, unit }) => (
  <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-4 transition-transform hover:scale-105">
    <p className="text-[10px] font-black text-emerald-200 uppercase tracking-widest mb-1">{label}</p>
    <div className="flex items-baseline gap-1">
      <span className="text-xl font-black text-white">{value || "--"}</span>
      <span className="text-[10px] font-bold text-emerald-300">{unit}</span>
    </div>
  </div>
);

const ActionButton = ({ icon: Icon, label, color, isLocked, onClick }) => (
  <motion.button
    whileHover={{ y: -5 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`${color} relative p-6 md:p-8 rounded-[32px] flex flex-col items-center justify-center gap-3 font-black text-xs uppercase tracking-tighter shadow-sm transition-all border border-transparent hover:shadow-lg`}
  >
    {isLocked && (
      <div className="absolute top-3 right-3 text-gray-400">
        <Shield size={12} />
      </div>
    )}
    <Icon size={28} />
    {label}
  </motion.button>
);

const SettingItem = ({ icon: Icon, label, onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ x: 10 }}
    className="w-full flex items-center space-x-4 p-5 rounded-2xl hover:bg-gray-50 transition-all group"
  >
    <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center group-hover:bg-white group-hover:shadow-md transition-all">
      <Icon size={20} className="text-gray-400 group-hover:text-emerald-500" />
    </div>
    <span className="flex-1 text-left font-bold text-sm text-gray-700">{label}</span>
    <ChevronRight size={18} className="text-gray-300 group-hover:text-emerald-500" />
  </motion.button>
);

export default Profile;